/**
 * Turns a scheduler record into what the screens need.
 *
 * The other direction lives in buildSchedulePayload.js; this is the way back.
 *
 * ── The two GETs return different amounts ───────────────────────────────────
 * /v1/scheduler/id returns the whole record. /v1/scheduler/all returns a
 * subset, missing:
 *
 *   Subject, Message, FileType, TaskType, TimeZone, ValidFrom, ValidTo,
 *   ExecutionLimit, ScheduleEndCondition, VerticalName, ApplicationID,
 *   ScheduleCreatedBy, APIRoute, APIMethod, PhoneNumbers,
 *   CreatedBy / CreatedDate / ModifiedBy / ModifiedDate
 *
 * So the list page (fed by /all) shows an em dash for those rather than a blank
 * that would read as "empty" instead of "not returned", while the edit form
 * (fed by /id) fills in properly. When /all grows to match, the dashes go away
 * on their own.
 *
 * IsDraft is dropped by the service entirely — sent on create, absent from both
 * responses.
 */

import { MONTHS, ORDINALS, WEEKDAYS, labelOf } from "./RecurrencePatternsForms/options";
import { licensedAppIdFor } from "../../utils/appAliases";

/** Shown wherever the API does not return a value we display. */
export const NOT_RETURNED = "—";

const value = (v) => (v === 0 || v ? v : NOT_RETURNED);

/* "14:45" -> "02:45 PM", for the parts of the UI that show a 12h clock. */
function to12Hour(runAt) {
  const [rawHour, rawMinute] = String(runAt || "").split(":");
  const hour24 = Number(rawHour);
  if (Number.isNaN(hour24)) return "";
  const hour = String(((hour24 + 11) % 12) + 1).padStart(2, "0");
  return `${hour}:${rawMinute || "00"} ${hour24 >= 12 ? "PM" : "AM"}`;
}

/* A block the service actually filled in, rather than one of the empties it returns
   alongside it — a record commonly comes back carrying every block, with all but one
   holding blank strings and zeroes. */
const hasContent = (block) => Boolean(block && (block.StartAt || customRecurrenceType(block)));

/* "monday" -> "Mon". The names arrive lowercased from weeklyDayNames, and the Custom
   branch of describeSchedule abbreviates its own days the same way. */
const shortDay = (name) => String(name || "").charAt(0).toUpperCase() + String(name || "").slice(1, 3);

/**
 * Whether this is a custom schedule — one built in the Custom Recurrence dialog.
 *
 * Normally just `RecurrencePattern === "Custom"`: the pattern names the block that holds
 * the rule, confirmed by the scheduler team on 2026-09-17.
 *
 * The second clause is for one week of bad records. Between 2026-09-11 and 2026-09-18
 * this app sent the cadence as the pattern — "Daily", "Weekly" — while the rule stayed
 * in the Custom block, so those records name a block that is empty and keep their real
 * data next door. They are still in the database, and read by the pattern alone they
 * would open in the edit form as a plain daily schedule and lose their interval, days and
 * end date on the next save.
 *
 * So: the named block wins when it has anything in it, and only a named block that came
 * back empty falls through to Custom.
 */
export function isCustomSchedule(record) {
  if (!record) return false;

  const pattern = record.RecurrencePattern;
  if (pattern === "Custom") return true;
  if (pattern === "NotRepeat") return false;

  return !hasContent(record.Recurrence?.[pattern]) && hasContent(record.Recurrence?.Custom);
}

/**
 * The block a record's rule actually lives in.
 *
 * RecurrencePattern names it, so that is tried first. The fallbacks matter because the
 * service returns all six blocks with the unused five full of blank strings and zeroes —
 * picking one by name alone can hand back an empty object that reads as a schedule with
 * no time and no days.
 */
export function activeRecurrence(record) {
  const recurrence = record?.Recurrence || {};
  const named = recurrence[record?.RecurrencePattern];

  if (hasContent(named)) return named;
  if (hasContent(recurrence.Custom)) return recurrence.Custom;

  return named || recurrence.Custom || recurrence.NotRepeat || {};
}

/**
 * The cadence inside a Custom block — "Daily", "Weekly", "Monthly" or "Yearly".
 *
 * Two spellings, because the scheduler team renamed the key on 2026-09-11: it was
 * `RecurrenceType` and is now `RecurrencePattern`. Every schedule saved before that date
 * still carries the old one, so both are read — otherwise the list's Frequency column,
 * the edit form's Repeat field and the upcoming-runs calendar would all go blank for
 * every existing schedule.
 *
 * Note this is a different thing from the record's top-level RecurrencePattern, which
 * says which block to read ("Custom" or "NotRepeat") rather than how often it runs. The
 * rename gave one name to both.
 */
export function customRecurrenceType(block) {
  return block?.RecurrencePattern || block?.RecurrenceType || "";
}

/**
 * One line describing when a schedule runs — the "Frequency & Time" column.
 * Mirrors describeRecurrence in CustomRecurrenceModal, but reads the API's
 * shape rather than the form's.
 */
export function describeSchedule(record) {
  const pattern = record?.RecurrencePattern;
  const block = activeRecurrence(record);
  const at = block.RunAt ? ` at ${to12Hour(block.RunAt)}` : "";

  if (pattern === "NotRepeat") {
    return block.StartAt ? `Once on ${block.StartAt}${at}` : "Does not repeat";
  }

  if (!isCustomSchedule(record)) {
    /* One of the simple blocks — Daily, Weekly, Monthly, Annually.

       Weekly names its days, and saying so is the only thing that separates an "Every
       weekday" schedule from an ordinary weekly one in this column. Both would otherwise
       read "Weekly at 07:50". Days are abbreviated the same way the Custom branch does
       below, so the two look alike in the table. */
    if (pattern === "Weekly") {
      const days = weeklyDayNames(block);
      if (days.length) {
        return `Weekly on ${days.map(shortDay).join(", ")}${at}`;
      }
    }

    return `${pattern || "Unknown"}${at}`;
  }

  const every = Number(block.RecurrenceInterval) || 1;
  const unit = { Daily: "day", Weekly: "week", Monthly: "month", Yearly: "year" }[customRecurrenceType(block)] || "time";
  const base = every === 1 ? `Every ${unit}` : `Every ${every} ${unit}s`;

  if (customRecurrenceType(block) === "Weekly" && block.RepeatOnDays?.length) {
    return `${base} on ${block.RepeatOnDays.map((d) => d.slice(0, 3)).join(", ")}${at}`;
  }

  const onDays = block.MonthlyOnDays || {};

  /* Yearly names its month. Custom has no Month field, so the payload carries the
     month — and, for "on day N", the day — in StartAt (see startOfTargetMonth in
     buildSchedulePayload); recurrenceFromCustom reads it back the same way for
     the edit form. Without this the column said "Every year at 09:00 PM" for
     "On November 1", and dropped "of October" from "the second Sunday". */
  if (customRecurrenceType(block) === "Yearly") {
    const start = String(block.StartAt || "");
    const month = MONTHS[Number(start.slice(5, 7)) - 1]?.label;
    const day = Number(start.slice(8, 10));
    if (onDays.Week && onDays.Day) {
      return `${base} on the ${onDays.Week} ${onDays.Day}${month ? ` of ${month}` : ""}${at}`;
    }
    if (month && day) {
      return `${base} on ${month} ${day}${at}`;
    }
  }

  if (onDays.Week && onDays.Day) {
    // Week holds the ordinal and Day the weekday name in this mode.
    return `${base} on the ${onDays.Week} ${onDays.Day}${at}`;
  }
  if (onDays.Day) {
    return `${base} on day ${onDays.Day}${at}`;
  }

  return `${base}${at}`;
}

/**
 * "Draft" or "Published", as the table's Status pill and the stat cards want it.
 *
 * Reads the scheduler's Status field, which it added so both live in one table. Compared
 * case-insensitively: the field is sent lowercase and there is no promise it comes back
 * that way.
 */
export function statusOf(record) {
  const status = String(record?.Status || "").toLowerCase();
  if (status === "published") return "Published";
  if (status === "draft") return "Draft";

  // pre-Status records - see the note where this is used
  return record?.IsActive ? "Published" : "Draft";
}

/**
 * A date the scheduler may or may not have set, for display.
 *
 * ── IN THE SCHEDULE'S OWN TIMEZONE, NOT THE VIEWER'S ────────────────────────
 * The scheduler returns UTC ("2026-09-11T17:00:00Z") and each log record says which zone
 * the schedule runs in. Rendered in the viewer's zone instead, a schedule set to 18:00
 * Europe/London showed as 22:30 to someone in Asia/Colombo — a time nobody configured
 * and which matches nothing on the Create Schedule form.
 *
 * So `timeZone` is passed through to Intl, which does the conversion natively — no
 * moment-timezone needed, and the repo has no such dependency to add.
 *
 * An unknown or misspelt zone would make Intl throw, which is why the conversion is
 * guarded: a bad zone falls back to the viewer's own rather than blanking the column.
 *
 * ── AND THE ZERO TIMESTAMP ──────────────────────────────────────────────────
 * "0001-01-01T00:00:00Z" is what the service writes for a date it does not have, and it
 * parses as a real date in the year 1 — printed raw it reads as a genuine timestamp from
 * two thousand years ago. Anything at or before 1970 is treated as absent.
 */
export function displayDate(value, timeZone) {
  if (!value) return NOT_RETURNED;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime()) || parsed.getFullYear() <= 1970) return NOT_RETURNED;

  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  try {
    return new Intl.DateTimeFormat("en-CA", { ...options, timeZone: timeZone || undefined })
      .format(parsed)
      .replace(",", "");
  } catch {
    // Intl rejects a zone it does not recognise; the date still matters more than the zone
    return new Intl.DateTimeFormat("en-CA", options).format(parsed).replace(",", "");
  }
}

/**
 * One run of a schedule, from POST /v1/scheduler/task/logs, as the logs table wants it.
 *
 * ── THE RESPONSE CHANGED ON 2026-09-12 ──────────────────────────────────────
 * We reported that every row of a schedule showed one identical date, 27 hours after the
 * delivery it sat beside. It was the schedule's *next* due time, repeated on each run.
 * The scheduler team split it in two and added a per-run id:
 *
 *   TaskLogID          unique per run           (new)
 *   TaskDate           when this run happened   (new)
 *   NextScheduledDate  when the schedule is next due — the old `ScheduledDate`, renamed
 *
 * The old names are still read as a fallback so the page does not go blank if it meets a
 * service that has not been redeployed, but nothing is expected to send them any more.
 *
 * `TaskID` is deliberately no longer part of the key. It looked like a run id and is not:
 * the scheduler team confirmed it is unique per *schedule*, which is why every row of one
 * schedule shared a value. TaskLogID is the real per-run id.
 *
 * Still nothing behind them in the response:
 *   - the delivery's user name: UserLogs carries Email and no name, and will not gain one,
 *     so the table no longer has a User column to leave empty
 *   - MailResponse ("202"): returned, but there is nowhere on the table for it
 */
export function rowFromScheduleLog(record, index = 0) {
  /* Every time on this row is shown in the schedule's own zone, including the delivery
     times in the expanded panel — so a run and its delivery read as the same clock rather
     than one converted and one not. */
  const zone = record?.TimeZone || "";

  const deliveries = (record?.UserLogs || []).map((entry, entryIndex) => ({
    id: `${entry?.MessageID || "delivery"}-${entryIndex}`,
    email: value(entry?.Email),
    scheduleType: value(entry?.ScheduleType),
    deliveredAt: displayDate(entry?.DeliveredDateTime, zone),
    status: value(entry?.DeliveryStatus),
    /* Not shown anywhere yet - an HTTP-ish code from the mail service ("202"), kept so a
       "why did this fail" column has something to read when one is added. */
    mailResponse: entry?.MailResponse || "",
  }));

  return {
    /* TaskLogID alone would do, but the index stays as a tiebreak: a duplicate key is a
       silent bug where React reuses the wrong row, and this costs nothing to prevent. */
    id: `${record?.TaskLogID || record?.ScheduleID || "log"}-${index}`,
    taskLogId: record?.TaskLogID || "",
    scheduleId: record?.ScheduleID || "",
    /* Kept on the row, but it names the schedule rather than this run - do not key on it. */
    taskId: record?.TaskID || "",

    reportName: value(record?.ReportName),
    frequency: value(record?.Frequency),
    fileType: value(record?.FileType),
    timeZone: value(record?.TimeZone),

    /* When this run happened. In the sample it matches DeliveredDateTime to the
       millisecond, so the two columns can read the same — asked the scheduler team
       whether it marks the start of the run or the moment the mail went out. */
    taskDate: displayDate(record?.TaskDate, zone),

    /* When the schedule is next due. The same value on every row of a schedule, which is
       correct now it says so: it describes the schedule, not the run. */
    nextScheduledDate: displayDate(record?.NextScheduledDate || record?.ScheduledDate || record?.EmailScheduledDate, zone),

    status: value(record?.Status || record?.status),

    deliveries,
  };
}

/**
 * The partial /all record filled in from the full /id record.
 *
 * Merged as records rather than as rows, so rowFromSchedule normalises once — including
 * the Timezone / TimeZone spelling split between the two endpoints, which is exactly the
 * kind of divergence a blind row-level merge would get wrong.
 *
 * Only keys the full record actually carries win. `undefined`, `null`, `""` and `[]` are
 * dropped, so a value /all did return cannot be wiped by one /id left blank. `false` and
 * `0` are kept — `IsActive: false` and `ExecutionLimit: 0` both mean something.
 *
 * Nested objects are not deep-merged on purpose: if /id returns a Recurrence it wins
 * whole. It is the authoritative record, and a Recurrence with its interval from one
 * response and its Ends from the other would be worse than either on its own.
 */
export function mergeScheduleRecords(partial, full) {
  if (!full) return partial || null;
  if (!partial) return full;

  const carried = Object.entries(full).filter(
    ([, v]) => v !== undefined && v !== null && v !== "" && !(Array.isArray(v) && v.length === 0),
  );

  return { ...partial, ...Object.fromEntries(carried) };
}

/**
 * When a schedule was created, as a sortable number.
 *
 * CreatedDate first. Where it is missing or the zero timestamp - which is what older
 * records carry - the id is used instead: a Mongo ObjectId's first eight hex characters
 * are its creation time in seconds, so an id alone dates a record.
 *
 * Returns 0 for anything undatable, which sorts it to the bottom rather than throwing
 * the order off.
 */
export function createdTimestamp(record) {
  const created = record?.CreatedDate;
  if (created) {
    const ms = new Date(created).getTime();
    // "0001-01-01T00:00:00Z" parses fine but is not a real date - treat it as absent
    if (!Number.isNaN(ms) && ms > 0) return ms;
  }

  const id = String(record?.ID || record?.ScheduleID || "");
  if (/^[0-9a-f]{24}$/i.test(id)) return parseInt(id.slice(0, 8), 16) * 1000;

  return 0;
}

/** A row for the Scheduled Reports table and the summary panel. */
export function rowFromSchedule(record) {
  const block = activeRecurrence(record);

  return {
    id: record.ID || record.ScheduleID,
    raw: record, // the partial /all record, kept in case a caller wants it

    reportName: record.ReportName || NOT_RETURNED,
    reportId: record.ReportID || "",

    fileType: value(record.FileType),
    scheduleType: value(record.TaskType),

    /* `Timezone`, not `TimeZone`. The scheduler spells it with a lowercase z, and
       reading the capitalised form found nothing - so the Time Zone column showed a dash
       even on schedules that had one. Both are read, since the doc uses TimeZone and
       there is no promise the response will not change to match it. */
    timeZone: value(record.Timezone || record.TimeZone),

    createdBy: value(record.ScheduleCreatedBy || record.CreatedBy),

    /* `vertical` is gone. It came from VerticalName, which the payload no longer sends
       and the UI can no longer supply, so the badge beside the file type was a permanent
       dash.

       The application would be the natural thing to put there instead, but a schedule
       record carries no ApplicationID - only ServiceName, which is now "WitmegReport"
       on every schedule and says nothing about the report. Getting it would mean the
       list page loading boards and the app registry to walk
       ReportID -> board.appList -> registry Name: two extra fetches for one label.
       Worth asking the scheduler team for ApplicationID on the record first. */

    frequency: describeSchedule(record),
    scheduledDateTime: block.StartAt ? `${block.StartAt} at ${block.RunAt || ""}`.trim() : NOT_RETURNED,

    /* For "newest first" on the list. CreatedDate is the right field but older records
       carry the zero timestamp, so the id is the fallback: a Mongo ObjectId holds its
       creation time in the first eight hex characters, which gives every record
       something real to sort by. */
    createdTs: createdTimestamp(record),

    /* The scheduler's Status field - "draft" or "published" - which is what makes a
       draft distinguishable from a paused schedule.

       IsActive is the fallback for schedules created before Status existed. It is a poor
       one: a published schedule someone switched off reads as a draft. Nothing better is
       available for those records, and it stops mattering as they age out. */
    status: statusOf(record),

    recipients: record.EmailID || [],

    /* No upcomingDates here. The summary panel works them out from `raw` with
       expandRecurrence, for the one schedule it opens — see the note on that module.
       Two reasons it is not done for every row on load:

         - a legacy schedule's end date lives in ValidTo / ExecutionLimit, which only
           GET /id returns, so a list row cannot be expanded correctly anyway
         - a daily rule builds ~366 dates; doing that per row on every load, and again
           after every delete, is a lot of work for the one row anybody opens */
  };
}

/* ── MATCHING A STORED VALUE BACK TO AN OPTION ──
   The payload lowercases FileType on the way out, so "PDF" is stored as "pdf" and no
   longer equals the option it came from. Rather than trusting the case, the stored value
   is matched against the known options and the option's own spelling is used.

   Listed here rather than imported from the page: the page imports this module, and
   importing it back would make a cycle. Six short strings that change rarely - if they
   drift, the field falls back to the form's default rather than showing something wrong. */
/* Must match FILE_TYPES in CreateSchedule.js. Link is gone (a public URL to private
   data) and JPEG became PNG. An old schedule still storing "jpeg" or "link" matches
   nothing here and falls back to the form's default, which is the right outcome — better
   a working default than a file type we no longer produce. */
export const FILE_TYPE_VALUES = ["PDF", "HTML", "Xls", "PNG", "CSV"];
export const SCHEDULE_TYPE_VALUES = ["Email", "WhatsApp", "SMS"];

/* Mirrors isPhoneChannel in CreateSchedule.js. Kept local for the same reason as the two
   lists above - the page imports this module, so importing the page back would be a
   cycle. */
export const isPhoneTask = (taskType) => taskType === "WhatsApp" || taskType === "SMS";

/**
 * A stored value matched back to one of the option strings, ignoring case.
 *
 * The API stores what it was sent, lowercased ("pdf"), while the selects carry display
 * forms ("PDF", "Xls"). Used raw, a stored value matches no option and the field silently
 * comes back empty - which is how the edit form used to lose its File Type on every open.
 *
 * Exported alongside the lists because ShareReportModal has exactly the same problem with
 * the file type on a log row.
 */
export const matchOptionValue = (options, stored) => {
  const needle = String(stored || "").trim().toLowerCase();
  if (!needle) return "";
  return options.find((o) => o.toLowerCase() === needle) || "";
};

/* The form's option values are short codes ("mon", "jan"); the API stores full
   names. These map back. */
const codeOf = (list, label) =>
  (list.find((item) => item.label.toLowerCase() === String(label || "").toLowerCase()) || {}).value || "";

/**
 * A scheduler record back into Create Schedule form state, for edit mode.
 *
 * `base` is emptyForm() from the page, so anything the API does not return
 * keeps the form's own default rather than becoming undefined.
 */
export function formFromScheduleRecord(record, base) {
  const form = { ...base };
  if (!record) return form;

  const block = activeRecurrence(record);

  /* Application re-selects itself now. The select's values are the registry's real
     ApplicationIDs, which is the same id space the API stores.

     Through licensedAppIdFor, because the two are not always the same id: a schedule
     stores the application the report belongs to, while the select offers the ones the
     user is licensed for, and those differ for an aliased application. The stored
     5ed4cc...4d2 matched no option and left the field blank - which also emptied the
     report list, since that is filtered by this. See utils/appAliases.js.

     Note only GET /id carries ApplicationID; GET /all does not, which is why the edit
     form loads the record by id rather than reusing the list row. */
  form.applicationId = licensedAppIdFor(record.ApplicationID || "");

  form.reportId = record.ReportID || "";
  form.date = block.StartAt || record.ValidFrom || "";
  // GET /id spells it TimeZone, GET /all spells it Timezone - read both
  form.timeZone = record.TimeZone || record.Timezone || base.timeZone;
  form.subject = record.Subject || "";
  form.message = record.Message || "";

  /* The payload sends FileType lowercased ("pdf"), and the select's values are the
     display forms ("PDF", "Xls"). So the stored value matched no option and the field
     came back empty on every edit. Matched back to the option rather than used raw. */
  form.fileType = matchOptionValue(FILE_TYPE_VALUES, record.FileType) || base.fileType;
  form.scheduleType = matchOptionValue(SCHEDULE_TYPE_VALUES, record.TaskType) || base.scheduleType;

  form.publisher = record.ScheduleCreatedBy || record.CreatedBy || "";

  /* Recipients follow the channel. A WhatsApp or SMS schedule keeps its people in
     PhoneNumbers, not EmailID — reading only EmailID opened such a schedule with an empty
     Send To, and saving from there would have wiped its real recipients.

     Both shapes are read because both exist: the current payload writes
     { Name, PhoneNumber } objects, while a bare string may sit in older records. */
  form.recipients = isPhoneTask(form.scheduleType)
    ? (record.PhoneNumbers || [])
        .map((entry) => (typeof entry === "string" ? { PhoneNumber: entry } : entry || {}))
        .map(({ Name, PhoneNumber }) => ({
          id: `phone-${PhoneNumber}`,
          name: Name || "",
          phone: PhoneNumber || "",
          external: true,
        }))
        .filter((r) => r.phone)
    : (record.EmailID || []).map((email) => ({
        id: `email-${email}`,
        name: email,
        email,
        external: true,
      }));

  if (block.RunAt) {
    const [rawHour, rawMinute] = String(block.RunAt).split(":");
    const hour24 = Number(rawHour);
    form.time = {
      hour: String(((hour24 + 11) % 12) + 1).padStart(2, "0"),
      minute: rawMinute || "00",
      meridiem: hour24 >= 12 ? "PM" : "AM",
    };
  }

  if (isCustomSchedule(record)) {
    form.repeat = "custom";
    form.recurrence = recurrenceFromCustom(block, form.time, base);
    form.range = rangeFromEnds(block.Ends, base.range);
  } else {
    /* One of the four simple patterns, each of which fills its own block. The Range of
       Recurrence for these lives at the top level (ValidTo / ExecutionLimit /
       ScheduleEndCondition), not in the block, so it is left as `base` gave it. */
    form.repeat = simpleRepeatOf(record, block);
    form.recurrence = null;
    if (form.repeat === "weekly") form.weeklyDay = weeklyDayCodeOf(block) || base.weeklyDay || "";
    /* ...and so it has to be READ from there. It was "left as base gave it",
       i.e. always No end date — a schedule published with "After 5 messages" or
       an End by date reopened showing No end date, though the payload had sent
       ScheduleEndCondition / ExecutionLimit / ValidTo correctly. */
    form.range = rangeFromTopLevel(record, base.range);
  }

  return form;
}

/* Monday to Friday, as "Every weekday" writes it — the set that tells that option apart
   from an ordinary weekly one. Compared as a set, since nothing promises an order. */
const WEEKDAY_SET = new Set(["monday", "tuesday", "wednesday", "thursday", "friday"]);

/* Every day named on a Weekly block, lowercased. The block carries a singular WeekDay and
   a plural WeekDays and we send both, but a record written by anything else may have only
   one of them — so both are read and merged. */
const weeklyDayNames = (block) => {
  const many = Array.isArray(block?.WeekDays) ? block.WeekDays : [];
  const all = [...many, block?.WeekDay].map((d) => String(d || "").trim().toLowerCase());
  return [...new Set(all.filter(Boolean))];
};

/**
 * Which simple Repeat option a record represents.
 *
 * The pattern gives the family and the Weekly block's days split it in two: exactly
 * Monday-to-Friday is the "Every weekday" option, anything else is an ordinary weekly
 * one. A Weekly block naming no days at all is still weekly — the day then comes from
 * the start date, which is what weeklyDayCodeOf falls back to.
 */
function simpleRepeatOf(record, block) {
  switch (record?.RecurrencePattern) {
    case "Daily":
      return "daily";
    case "Weekly": {
      const days = weeklyDayNames(block);
      const isMonToFri = days.length === WEEKDAY_SET.size && days.every((d) => WEEKDAY_SET.has(d));
      return isMonToFri ? "weekdays" : "weekly";
    }
    /* Monthly and Annually have no option of their own on the page - they can only be
       built in the Custom dialog. A record carrying one (from the ERP, or from before
       this app) opens as "Does not repeat" rather than silently becoming something else,
       and saving would then rewrite it. Worth revisiting if such records turn up. */
    default:
      return "none";
  }
}

/* A Weekly block's day as a WEEKDAYS code, for the select beside "Weekly on". Falls back
   to the start date's own weekday, which is the day such a schedule actually runs. */
function weeklyDayCodeOf(block) {
  const [first] = weeklyDayNames(block);
  if (first) return codeOf(WEEKDAYS, first);

  const parts = String(block?.StartAt || "").split("-");
  if (parts.length !== 3) return "";
  const [year, month, day] = parts.map(Number);
  if (!year || !month || !day) return "";

  /* Built from the parts rather than parsing the string: a bare "YYYY-MM-DD" is read as
     UTC midnight, which gives the previous day's weekday east of UTC. */
  const local = new Date(year, month - 1, day);
  return Number.isNaN(local.getTime()) ? "" : WEEKDAYS[local.getDay()]?.value || "";
}

/* Custom block -> the shape CustomRecurrenceModal edits. */
function recurrenceFromCustom(block, time, base) {
  const recurrence = { ...(base.recurrence || {}) };
  const interval = Number(block.RecurrenceInterval) || 1;
  const onDays = block.MonthlyOnDays || {};
  const isOrdinalMode = Boolean(onDays.Week);

  const mode = { Daily: "daily", Weekly: "weekly", Monthly: "monthly", Yearly: "yearly" }[customRecurrenceType(block)];

  return {
    ...recurrence,
    mode: mode || "daily",
    time,
    daily: { type: "every", interval },
    weekly: {
      interval,
      days: (block.RepeatOnDays || []).map((d) => codeOf(WEEKDAYS, d)).filter(Boolean),
    },
    monthly: {
      type: isOrdinalMode ? "the" : "day",
      day: Number(onDays.Day) || 1,
      interval,
      ordinal: isOrdinalMode ? codeOf(ORDINALS, onDays.Week) || "first" : "first",
      weekday: isOrdinalMode ? codeOf(WEEKDAYS, onDays.Day) || "sun" : "sun",
      theInterval: interval,
    },
    yearly: {
      type: isOrdinalMode ? "the" : "on",
      /* Custom has no Month field — StartAt carries it, so the month is read
         back off the start date. */
      month: monthCodeFrom(block.StartAt),
      day: Number(String(block.StartAt || "").slice(8, 10)) || 1,
      ordinal: isOrdinalMode ? codeOf(ORDINALS, onDays.Week) || "first" : "first",
      weekday: isOrdinalMode ? codeOf(WEEKDAYS, onDays.Day) || "sun" : "sun",
      theMonth: monthCodeFrom(block.StartAt),
    },
  };
}

function monthCodeFrom(isoDate) {
  const monthIndex = Number(String(isoDate || "").slice(5, 7)) - 1;
  return MONTHS[monthIndex]?.value || "jan";
}

/* Custom.Ends -> the page's Range of Recurrence radios. */
function rangeFromEnds(ends, base) {
  if (!ends) return base;
  if (ends.IsEndsNever) return { ...base, type: "none" };
  if (ends.EndAt) return { ...base, type: "endBy", endBy: ends.EndAt };
  if (ends.EndAfterOccurrences) return { ...base, type: "after", after: ends.EndAfterOccurrences };
  return base;
}

/* The top-level end fields -> the Range of Recurrence radios, for the simple
   patterns (whose blocks have no Ends). Mirrors what buildSchedulePayload sends:
   ScheduleEndCondition 1 = End by ValidTo, 2 = After ExecutionLimit, 3 = never.

   The condition decides when present. A record without one is read off the
   values instead — an ExecutionLimit above 0 or a ValidTo — so older records
   still come back right. ValidTo may return as a full timestamp; the date
   input wants just YYYY-MM-DD. */
function rangeFromTopLevel(record, base) {
  const condition = Number(record?.ScheduleEndCondition);
  const limit = Number(record?.ExecutionLimit) || 0;
  const validTo = String(record?.ValidTo || "").slice(0, 10);

  if (condition === 3) return { ...base, type: "none" };
  if (condition === 2 && limit > 0) return { ...base, type: "after", after: limit };
  if (condition === 1 && validTo) return { ...base, type: "endBy", endBy: validTo };

  if (!condition) {
    if (limit > 0) return { ...base, type: "after", after: limit };
    if (validTo) return { ...base, type: "endBy", endBy: validTo };
  }
  return base;
}

export { labelOf };
